/**
 * AVA-475 — number formatting shared by the report's two renderings.
 *
 * The HTML preview and the PDF are ONE document in two forms. They must agree
 * on every number, so the numeric conventions live here once and both import
 * them. Two independent implementations is how a report ends up disagreeing
 * with its own preview.
 *
 * They differ in exactly one respect, and only because they have to:
 *
 *   THE MINUS SIGN. `formatDelta` renders U+2212 MINUS SIGN, which is correct
 *   typography and correct in the DOM. jsPDF's built-in Helvetica encodes text
 *   as WinAnsi (cp1252), and U+2212 is NOT in cp1252 — a negative deviation
 *   would print with its sign silently dropped, turning -5 mm into 5 mm on a
 *   conformance document. So the PDF asks for the ASCII variant.
 *
 *   U+00B1 (±) IS in cp1252 and is safe in both. Never write '+/-'.
 */
import {
  formatDelta,
  formatMetres,
  isSignificantOffsetAxis,
} from '@/features/Verification/utils/pointMath';

/** Placeholder for any value that is absent or not a number. */
export const EMPTY = '—';

/**
 * SITE-metre position, at the shared `POSITION_DECIMALS` precision — one
 * decimal finer than the millimetre the deviation beside it is stated in.
 *
 * TAKES A NUMBER, AND ONLY A NUMBER. The value arrives already converted, on
 * `point.designSiteM` / `point.asBuiltSiteM` from `GET_REPORT_MODEL`. This
 * module resolves no units and imports nothing that could: a formatter that
 * reached for a viewer or a store would put the report's numbers back under
 * something Vue cannot observe, and the report is the one surface where a
 * confidently wrong number survives the session.
 *
 * Delegates rather than reimplementing: the screen and the report must agree
 * digit for digit, and two `toFixed` calls in two files is how they stop.
 * `ascii: true` because this string goes straight into the PDF — see the module
 * note on cp1252.
 *
 * @param {number} value SITE metres.
 * @returns {string}
 */
export function formatPosition(value) {
  return formatMetres(value, { ascii: true });
}

/**
 * Signed millimetre deviation, 1 decimal.
 *
 * Delegates to `formatDelta` so there is a single implementation of the numeric
 * convention, then swaps only the sign glyph. Do not reimplement the number
 * here — that is the drift this module exists to prevent.
 *
 * @param {number} mm
 * @param {boolean} [ascii] true for the PDF (see the module note on cp1252).
 * @returns {string}
 */
export function formatDeviation(mm, ascii = false) {
  const rendered = formatDelta(mm);
  return ascii ? rendered.replace('−', '-') : rendered;
}

/**
 * Tolerance with a real ± (U+00B1).
 * @param {number} mm
 * @returns {string}
 */
export function formatTolerance(mm) {
  return Number.isFinite(mm) ? `±${mm}` : EMPTY;
}

/** Survey-axis letters, in report order. */
const OFFSET_AXES = [
  { key: 'x', label: 'E' },
  { key: 'y', label: 'N' },
  { key: 'z', label: 'EL' },
];

/**
 * Describes an offset vector as `+0.005 E · -0.002 N`, listing only the axes
 * that actually carry one. Returns '' for no offset, so callers can treat the
 * empty string as "say nothing" — an offset folded silently into an adjusted
 * value would make the report unauditable, but a zero offset is not news.
 *
 * Significance uses the SHARED threshold, not `!== 0`. A 0.00004 m offset is
 * non-zero but renders as `+0.0000` — a stated adjustment with no visible
 * value, which on a conformance document reads as an error rather than as
 * precision. The threshold tracks the display precision; both live in
 * pointMath.js so they cannot move apart.
 *
 * ASCII sign throughout: this string goes into the PDF verbatim.
 *
 * @param {{x:number,y:number,z:number}|null} offset SITE-metre offset vector,
 *   already converted by the report clone. Scale only — an offset is a
 *   difference, so the model's globalOffset cancels.
 * @returns {string}
 */
export function describeOffset(offset) {
  if (!offset) return '';
  return OFFSET_AXES
    .filter((axis) => isSignificantOffsetAxis(offset[axis.key]))
    // `signed` because an offset is a change, not a position: `+0.0050 E`.
    // `ascii` because this string goes into the PDF verbatim.
    .map((axis) => `${formatMetres(offset[axis.key], { ascii: true, signed: true })} ${axis.label}`)
    .join(' · ');
}

/**
 * The full offset sentence for a point, covering both sides.
 *
 * Reads the `…SiteM` fields, not the raw `designOffset` / `asBuiltOffset` the
 * store holds: this sentence is printed directly beneath the position columns,
 * which are site metres, and a 5 mm correction stated as "+5.0000 E" beside a
 * position of 15.6400 is unreadable — the page would carry two units with
 * nothing to distinguish them. Both come off the report clone
 * (GET_REPORT_MODEL); this module converts nothing itself.
 *
 * @param {{designOffsetSiteM:object, asBuiltOffsetSiteM:object}} point
 * @returns {string} '' when neither side is offset.
 */
export function describePointOffsets(point) {
  const parts = [];
  const design = describeOffset(point?.designOffsetSiteM);
  const asBuilt = describeOffset(point?.asBuiltOffsetSiteM);
  if (design) parts.push(`Design offset ${design}`);
  if (asBuilt) parts.push(`As-built offset ${asBuilt}`);
  return parts.join('   ');
}

/**
 * Report axis order plus the keys that carry each axis's data on a derived
 * point. Shared so the preview and the PDF cannot label columns differently.
 */
export const REPORT_AXES = Object.freeze([
  Object.freeze({ axis: 'Eastings', key: 'x', delta: 'dX', pass: 'passX' }),
  Object.freeze({ axis: 'Northings', key: 'y', delta: 'dY', pass: 'passY' }),
  Object.freeze({ axis: 'Elevation', key: 'z', delta: 'dZ', pass: 'passZ' }),
]);
